import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import type { MedicalShipmentProto } from "@noom/noom-contracts/noom_contracts/healthcare/service/healthcare/medical_shipments_service";

import { getShippingData } from "../modules/shipping/getShippingData";

import { useAuthCredentials } from "./useAuthCredentials";

export const useShippingData = (): {
  shippingData: MedicalShipmentProto | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} => {
  const { accessCode, accessToken } = useAuthCredentials();
  const [searchParams] = useSearchParams();
  const userTreatmentId = searchParams.get("userTreatmentId");
  const [shippingData, setShippingData] = useState<MedicalShipmentProto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchShippingData = useCallback(async () => {
    if (!accessCode) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await getShippingData(
        accessCode,
        accessToken ?? "",
        userTreatmentId
      );
      setShippingData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch shipping data");
      setShippingData(null);
    } finally {
      setLoading(false);
    }
  }, [accessCode, accessToken, userTreatmentId]);

  useEffect(() => {
    fetchShippingData();
  }, [fetchShippingData]);

  return { shippingData, loading, error, refetch: fetchShippingData };
};
